/**
 * RecommendationGroundingPanel — inline "PRISM-grounded" panel on each rec card.
 *
 * Renders coverage %, matched-tools, named gap, and before/after action steps
 * when a rec has prism_grounding data attached (computed server-side by
 * recGrounding.js + RecommendationSelectorEngine).
 *
 * Cross-product purple accent (#a855f7) matches the Phase 5 narrative convention
 * already used on Foundations Board Report + Strategic Scenarios triangulation.
 *
 * Color-blind safe: uses shape + label + position alongside color.
 */

import React, { useState } from 'react';

// Slice A — structured step tokens for inline tool chips + bold facts.
export type GroundedStepToken =
  | { type: 'text'; value: string }
  | { type: 'tool'; value: string }
  | { type: 'fact'; value: string };

export interface GroundedStepV2 {
  text: string; // plain-text fallback (for accessibility / fallback render)
  tokens: GroundedStepToken[];
}

// Slice 2 Phase 2 — evidence-grounded coverage record from the
// PRISM rec-evidence-coverage aggregator (Roger-approved math, v3 memo).
// Optional sub-field on PrismGroundingData; present once Phase 3 wires the
// rec-evidence-coverage pipeline into recGrounding.js. The asset-class
// signal continues to render even when this is absent; when present, an
// additional citation-grade panel section renders below the asset-class
// chips with the deeper "your evidence covers X% of required controls"
// signal.
export interface EvidenceCoverageData {
  status: 'aggregated' | 'cold_start' | 'no_primary_reqs' | 'unresolved_caps';
  /** 0-100 capability-weighted coverage across primary-tier requirements.
   *  Null when status !== 'aggregated'. */
  coverage_pct: number | null;
  primary_req_count: number;
  primary_req_covered: number;
  supplemental_req_count: number;
  supplemental_req_covered: number;
  top_match: {
    requirement_id: string;
    req_score: number;
    evidence_name: string;
    evidence_id: number;
  } | null;
  top_gap: {
    requirement_id: string;
    cap_count: number;
    total_primary_caps: number;
  } | null;
  /** Pretty framework name (e.g., "CMMC 2.0") for display. */
  standard_label?: string;
  /** Deterministic DoDCAR-style rationale string — auditor-readable;
   *  cites standard, names source, ends with a number. */
  rationale: string;
}

export interface PrismGroundingData {
  required_classes: Array<{ code: string; name: string }>;
  matched_classes: Array<{ code: string; name: string }>;
  gap_classes: Array<{ code: string; name: string }>;
  matched_tools: Array<{ name: string; source: 'tool' | 'control'; effectiveness: number | null }>;
  coverage_pct: number;
  named_gap_text: string | null;
  original_action_steps: string[];
  grounded_action_steps: string[] | null;
  // Slice A — preferred when present. Backend (recGrounding.js
  // generateGroundedStepsV2) emits per-class substantive steps with inline
  // tool/fact tokens. Frontend renders tokens with chip styling.
  grounded_action_steps_v2?: GroundedStepV2[] | null;
  // Slice 2 Phase 2 — evidence-grounded coverage from PRISM
  // evidence_coverage_assessments. Optional; renders when present.
  evidence_coverage?: EvidenceCoverageData | null;
}

interface Props {
  grounding: PrismGroundingData;
  recTitle?: string;
}

const PURPLE = '#a855f7';
const PURPLE_SOFT = '#c4b5fd';
const PURPLE_BG = 'rgba(168, 85, 247, 0.06)';
const PURPLE_BORDER = 'rgba(168, 85, 247, 0.30)';
const PURPLE_BG_DEEP = 'rgba(168, 85, 247, 0.10)';

const CYAN = '#7dd3fc';
const GREEN = '#10b981';
const AMBER = '#f59e0b';
const RED = '#ef4444';

function coverageColor(pct: number): string {
  if (pct >= 80) return GREEN;
  if (pct >= 50) return AMBER;
  return RED;
}

/**
 * Slice 2 Phase 2 — evidence-grounded coverage section. Renders the
 * citation-grade rationale + status-aware UX (cold-start / no-primary /
 * unresolved-caps / aggregated). Visually distinct from the asset-class
 * chip cluster above: deeper indigo accent + auditor-shaped sentence layout.
 */
const EvidenceCoverageSection: React.FC<{ coverage: EvidenceCoverageData }> = ({ coverage }) => {
  const isAggregated = coverage.status === 'aggregated';
  const isColdStart = coverage.status === 'cold_start';

  const pctColor = isAggregated && coverage.coverage_pct !== null
    ? coverageColor(coverage.coverage_pct)
    : AMBER;

  return (
    <div
      style={{
        marginTop: 12,
        marginBottom: 8,
        padding: '10px 12px',
        background: 'rgba(99, 102, 241, 0.06)',
        border: '1px solid rgba(99, 102, 241, 0.30)',
        borderLeft: '3px solid #6366f1',
        borderRadius: 5,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 10,
          marginBottom: 6,
        }}
      >
        <span
          style={{
            background: 'rgba(99, 102, 241, 0.12)',
            border: '1px solid #6366f1',
            color: '#a5b4fc',
            fontSize: 9,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontWeight: 700,
            padding: '2px 7px',
            borderRadius: 3,
            whiteSpace: 'nowrap',
          }}
        >
          ◆ Evidence-Grounded
        </span>
        {isAggregated && coverage.coverage_pct !== null && (
          <>
            <span
              style={{
                color: pctColor,
                fontWeight: 700,
                fontSize: 13,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {coverage.coverage_pct}% capability-weighted
            </span>
            <span style={{ color: 'var(--ra-text-secondary, #ccd6f6)', fontSize: 11 }}>
              {coverage.primary_req_covered} of {coverage.primary_req_count} primary controls covered
              {coverage.supplemental_req_count > 0 &&
                ` · +${coverage.supplemental_req_covered}/${coverage.supplemental_req_count} supplemental`}
            </span>
          </>
        )}
        {isColdStart && (
          <span style={{ color: AMBER, fontSize: 11, fontWeight: 600 }}>
            ▲ Evidence library empty
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 12,
          lineHeight: 1.5,
          color: 'var(--ra-text-primary, #e6f1ff)',
        }}
      >
        {coverage.rationale}
      </div>
      <div
        style={{
          marginTop: 8,
          fontSize: 9,
          color: 'var(--ra-text-secondary, #6c7384)',
          fontFamily: '"SF Mono", Menlo, monospace',
          fontStyle: 'italic',
        }}
      >
        source: PRISM evidence_coverage_assessments × Foundations rec capability bridge
      </div>
    </div>
  );
};

const RecommendationGroundingPanel: React.FC<Props> = ({ grounding }) => {
  const [expanded, setExpanded] = useState(false);
  const {
    matched_classes,
    gap_classes,
    matched_tools,
    coverage_pct,
    named_gap_text,
    evidence_coverage,
  } = grounding;
  // Note: original_action_steps + grounded_action_steps + grounded_action_steps_v2
  // are also in `grounding` but consumed by the parent rec card now (single
  // Action Steps render in AssessmentDashboard.jsx).

  const covColor = coverageColor(coverage_pct);
  const hasGap = gap_classes.length > 0;
  // Slice A v2 step rendering moved to AssessmentDashboard's single Action
  // Steps section (no more Before/After comparison in this panel).
  // Variables `grounded_action_steps` and `original_action_steps` from the
  // grounding payload are now consumed by the parent rec card directly.

  return (
    <div
      style={{
        marginTop: 10,
        padding: '12px 14px',
        background: PURPLE_BG,
        border: `1px solid ${PURPLE_BORDER}`,
        borderLeft: `3px solid ${PURPLE}`,
        borderRadius: 6,
      }}
    >
      {/* Header row — badge + coverage + summary */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <span
          style={{
            background: PURPLE_BG_DEEP,
            border: `1px solid ${PURPLE}`,
            color: PURPLE_SOFT,
            fontSize: 9,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontWeight: 700,
            padding: '2px 7px',
            borderRadius: 3,
            whiteSpace: 'nowrap',
          }}
        >
          ◆ PRISM-Grounded
        </span>

        <span
          style={{
            color: covColor,
            fontWeight: 700,
            fontSize: 13,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {coverage_pct}% deployed
        </span>

        <span style={{ color: 'var(--ra-text-secondary, #ccd6f6)', fontSize: 11 }}>
          {matched_classes.length} of {matched_classes.length + gap_classes.length} required capabilities matched
        </span>

        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          style={{
            marginLeft: 'auto',
            background: 'transparent',
            border: `1px solid ${PURPLE_BORDER}`,
            color: PURPLE_SOFT,
            fontSize: 11,
            padding: '2px 10px',
            borderRadius: 4,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
          aria-expanded={expanded}
        >
          {expanded ? '▴ Hide grounding' : '▾ Show grounding'}
        </button>
      </div>

      {/* Compact one-liner gap when collapsed */}
      {!expanded && named_gap_text && (
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: 'var(--ra-text-secondary, #ccd6f6)',
            borderLeft: `2px solid ${hasGap ? RED : GREEN}`,
            paddingLeft: 8,
          }}
        >
          <strong style={{ color: hasGap ? RED : GREEN }}>
            {hasGap ? 'GAP: ' : ''}
          </strong>
          {named_gap_text}
        </div>
      )}

      {/* Expanded detail */}
      {expanded && (
        <div style={{ marginTop: 12 }}>
          {/* Tools matched */}
          {matched_tools.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div
                style={{
                  fontSize: 10,
                  color: 'var(--ra-text-secondary, #8892b0)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontWeight: 600,
                  marginBottom: 4,
                }}
              >
                ✓ Already deployed in your PRISM stack
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {matched_tools.map((t, i) => (
                  <span
                    key={i}
                    title={t.source}
                    style={{
                      background: 'rgba(125, 211, 252, 0.10)',
                      border: '1px solid rgba(125, 211, 252, 0.30)',
                      color: CYAN,
                      fontSize: 11,
                      padding: '1px 7px',
                      borderRadius: 3,
                      fontWeight: 500,
                    }}
                  >
                    {t.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Gap classes */}
          {gap_classes.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div
                style={{
                  fontSize: 10,
                  color: RED,
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  fontWeight: 700,
                  marginBottom: 4,
                }}
              >
                ⚠ Missing from your stack
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {gap_classes.map((g, i) => (
                  <span
                    key={i}
                    style={{
                      background: 'rgba(239, 68, 68, 0.10)',
                      border: '1px solid rgba(239, 68, 68, 0.35)',
                      color: RED,
                      fontSize: 11,
                      padding: '1px 7px',
                      borderRadius: 3,
                      fontWeight: 500,
                    }}
                  >
                    {g.name}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action steps now render once in the rec card (AssessmentDashboard) —
              PRISM-grounded v2 when available, original generic otherwise.
              Removed the Before/After comparison block per Pat 2026-05-19 demo
              review: comparison framing reads as demo theater rather than
              customer UX. Single best-available action list lives in the rec
              card; this panel keeps coverage / matched-tools / gap-classes. */}

          {/* Slice 2 Phase 2 — evidence-grounded coverage section. Renders only
              when the PRISM rec-evidence-coverage aggregator produced output
              for this rec (Phase 3 wire-up). Asset-class section above is the
              shallow signal ("you own Splunk"); this is the deep signal
              ("your Splunk's evidence covers AC.L2-3.1.5 at 90%"). */}
          {evidence_coverage && <EvidenceCoverageSection coverage={evidence_coverage} />}

          {/* Footer cite */}
          <div
            style={{
              marginTop: 12,
              fontSize: 9,
              color: 'var(--ra-text-secondary, #6c7384)',
              fontFamily: '"SF Mono", Menlo, monospace',
              fontStyle: 'italic',
            }}
          >
            source: PRISM deployed tools/controls × Foundations rec capability-class map
          </div>
        </div>
      )}
    </div>
  );
};

// Slice A — render a single grounded step from its token stream. Text tokens
// render inline; tool tokens render as cyan chips; fact tokens render bold.
// Falls back to plain text if tokens are malformed.
//
// Exported so AssessmentDashboard.jsx can use it to render the rec card's
// single Action Steps section with PRISM-grounded v2 tokens when available.
export const GroundedStepLine: React.FC<{
  tokens: GroundedStepToken[];
  fallback?: string;
}> = ({ tokens, fallback }) => {
  if (!Array.isArray(tokens) || tokens.length === 0) {
    return <span>{fallback || ''}</span>;
  }
  return (
    <span>
      {tokens.map((t, i) => {
        if (t.type === 'tool') {
          return (
            <span
              key={i}
              style={{
                display: 'inline-block',
                background: 'rgba(125, 211, 252, 0.10)',
                border: '1px solid rgba(125, 211, 252, 0.30)',
                color: CYAN,
                padding: '0 6px',
                borderRadius: 3,
                fontSize: 11,
                fontWeight: 500,
                margin: '0 2px',
                lineHeight: 1.5,
              }}
            >
              {t.value}
            </span>
          );
        }
        if (t.type === 'fact') {
          return (
            <strong
              key={i}
              style={{
                color: 'var(--ra-text-primary, #e6f1ff)',
                fontWeight: 600,
              }}
            >
              {t.value}
            </strong>
          );
        }
        // type: 'text' (default)
        return <React.Fragment key={i}>{t.value}</React.Fragment>;
      })}
    </span>
  );
};

export default RecommendationGroundingPanel;
